import { RouteModule } from '#/horizon/components/RouteModule';
import { getServerRouterContext } from '#/horizon/router-contexts/server-router-context';
import { useI18n } from '#/i18n/hooks/useI18n';
import { I18nLocale } from '#/i18n/types/I18nLocale';
import type {
  LayoutHandleElements,
  LayoutHandleProps,
} from '#/layouts/Layout/LayoutHandleProps';
import { DynamicSearchOrgsRefinements } from '#/modules/search/orgs/components/Refinements/DynamicSearchOrgsRefinements';
import { searchOrgsRoutePathByLocale } from '#/modules/search/orgs/routing/searchOrgs.route-paths';
import { searchOrgsRouteReference } from '#/modules/search/routing/search.routes';
import { getSearchPageLinkRels } from '#/modules/search/utils/getSearchPageLinkRels';
import type { HorizonAppRouteLoaderArgs } from '#/routing/classes/types/AppRouteLoaderArgs';
import type { HorizonAppRouteComponentProps } from '#/routing/types/HorizonAppRouteComponentProps';
import { getStaticImageSrc } from '#/utils/getImageSrc';

import { SearchOrgsPage } from './SearchOrgsPage';

export const handle = {
  searchRefinements: <DynamicSearchOrgsRefinements />,
} satisfies LayoutHandleElements;

export function loader({
  url,
  context,
}: HorizonAppRouteLoaderArgs<typeof searchOrgsRouteReference>) {
  const { serverContext } = getServerRouterContext(context);

  return {
    handle: {
      urlByLocale: searchOrgsRoutePathByLocale,
      darkBackground: true,
      defaultSearchType: 'ORG',
      hideFooterDivider: true,
      showDesktopSearch: true,
      searchFromPageType: 'SEARCH',
      showSearchRefinements: true,
      isSearchPage: true,
      hideMobileHeaderOnScroll: true,
    } satisfies LayoutHandleProps,

    linkRels: getSearchPageLinkRels({
      searchParams: url.searchParams,
      locale: serverContext.locale,
      routePathByLocale: searchOrgsRoutePathByLocale,
      referenceRoute: searchOrgsRouteReference,
    }),

    now: serverContext.now,
  };
}

export default function SearchOrgsHorizonRoute({
  loaderData: { linkRels, now },
}: HorizonAppRouteComponentProps<typeof loader>) {
  const { i18n, locale } = useI18n();

  const ogImage = (() => {
    switch (locale) {
      case I18nLocale.EN:
        return getStaticImageSrc('og-image-en-organizations.jpg');
      case I18nLocale.ES:
        return;
      case I18nLocale.PT:
        return getStaticImageSrc('og-image-pt-organizations.jpg');
    }
  })();

  return (
    <RouteModule
      title={
        /* TRANSLATORS: org search page title */
        i18n('Discover Nonprofits & Social-Impact Organizations')
      }
      description={
        /* TRANSLATORS: org search page description */
        i18n(
          'Discover thousands of nonprofits, NGOs, and social-impact organizations. Search by location, cause area, or organization type to find groups making a difference',
        )
      }
      ogImage={ogImage}
      linkRels={linkRels}
    >
      <SearchOrgsPage now={now} />
    </RouteModule>
  );
}
